import { ReactNode, useEffect, useState, useMemo, useCallback } from "react";

import UserLink from "../common/UserLink";
import LogDetailsModal from "./LogDetailsModal";
import useUrlSort from "../../../hooks/useUrlSort";
import CustomTable from "../../common/tables/Table";
import { useUrl } from "../../../hooks/urlEncoding";
import { ITableSchema } from "../../../utils/types";
import { routerPaths } from "../../../constants/Paths";
import { useCanAccess } from "../../../hooks/canAccess";
import errorResolver from "../../../utils/error-resolver";
import useTableSchema from "../../../hooks/useTableSchema";
import { AccessEnums } from "../../../constants/accessEnum";
import { useLangContext } from "../../../context/langContext";
import TextFilter from "../../common/tables/filters/TextFilter";
import { formatDateWithTime } from "../../../utils/date-formatter";
import NumberFilter from "../../common/tables/filters/NumberFilter";
import DateTimeFilter from "../../common/tables/filters/DateTimeFilter";
import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import SelectionFilter from "../../common/tables/filters/SelectionFilter";
import { useNotificationContext } from "../../../context/notificationContext";
import {
  SortDirectionEnum,
  FilterOperatorEnum,
} from "../../../services/Schemas/ListQueryString";
import { LogListRequest } from "../../../services/Endpoints/logs/LogListEndpoints";
import {
  AccessTokenListColumnSortable,
  AccessTokenListColumnFilterable,
} from "../../../services/Endpoints/access-tokens/AccessTokensEndpoint";
import {
  UsersListColumnSortable,
  UsersListColumnFilterable,
} from "../../../services/Endpoints/users/UsersListEndpoint";
import {
  WorkspaceListColumnSortable,
  WorkspaceListColumnFilterable,
} from "../../../services/Endpoints/workspaces";
import {
  PartnerListColumnSortable,
  PartnerListColumnFilterable,
} from "../../../services/Endpoints/partners/PartnerEndpoint";
import InfoBadge, { OriginEnum } from "../../common/info-badge/InfoBadge";

import { useLocation } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";

export interface ILog {
  id: number;
  url: ReactNode;
  user_id: number;
  track_code: string;
  partner_id: number;
  created_at: string;
  workspace_id: number;
  response_code: number;
  request_method: string;
  user: null | ReactNode;
  access_token_id: number;
  duration_millisecond: number;
}

function buildLogRows(
  logs: any[],
  seenLogIds: Set<number>,
  can: ReturnType<typeof useCanAccess>["can"],
  handleOpenLogModal: (id: number) => void,
  queryStringUrl: ReturnType<typeof useUrl>["queryStringUrl"],
): ILog[] {
  return logs.map(
    ({
      id,
      url,
      user,
      model,
      user_id,
      track_code,
      created_at,
      partner_id,
      workspace_id,
      response_code,
      request_method,
      access_token_id,
      reference_model,
      duration_millisecond,
      requested_at_millisecond,
    }) => ({
      id,
      model,
      created_at,
      track_code,
      request_method,
      reference_model,
      duration_millisecond,
      requested_at_millisecond,
      access_token_id: access_token_id && (
        <UserLink
          user={{ id: access_token_id }}
          disabled={!can(AccessEnums.AdminAccessTokensList)}
          link={queryStringUrl(routerPaths.ACCESS_TOKEN, {
            filters: {
              [AccessTokenListColumnFilterable.ID]: {
                value: access_token_id,
                operator: FilterOperatorEnum.EQUAL,
                column: AccessTokenListColumnFilterable.ID,
              },
            },
            sorts: {
              [AccessTokenListColumnSortable.ID]: {
                direction: SortDirectionEnum.DESC,
                column: AccessTokenListColumnSortable.ID,
              },
            },
          })}
        />
      ),
      user_id: user_id && (
        <UserLink
          user={{ id: user_id }}
          disabled={!can(AccessEnums.AdminUsersList)}
          link={queryStringUrl(routerPaths.USERS, {
            filters: {
              [UsersListColumnFilterable.ID]: {
                value: user_id,
                operator: FilterOperatorEnum.EQUAL,
                column: UsersListColumnFilterable.ID,
              },
            },
            sorts: {
              [UsersListColumnSortable.ID]: {
                direction: SortDirectionEnum.DESC,
                column: UsersListColumnSortable.ID,
              },
            },
          })}
        />
      ),
      workspace_id: workspace_id && (
        <UserLink
          user={{ id: workspace_id }}
          disabled={!can(AccessEnums.AdminWorkspacesList)}
          link={queryStringUrl(routerPaths.WORKSPACES, {
            filters: {
              [WorkspaceListColumnFilterable.ID]: {
                value: workspace_id,
                operator: FilterOperatorEnum.EQUAL,
                column: WorkspaceListColumnFilterable.ID,
              },
            },
            sorts: {
              [WorkspaceListColumnSortable.ID]: {
                direction: SortDirectionEnum.DESC,
                column: WorkspaceListColumnSortable.ID,
              },
            },
          })}
        />
      ),
      partner_id: partner_id && (
        <UserLink
          user={{ id: partner_id }}
          disabled={!can(AccessEnums.AdminPartnerList)}
          link={queryStringUrl(routerPaths.PARTNERS, {
            filters: {
              [PartnerListColumnFilterable.ID]: {
                value: partner_id,
                operator: FilterOperatorEnum.EQUAL,
                column: PartnerListColumnFilterable.ID,
              },
            },
            sorts: {
              [PartnerListColumnSortable.ID]: {
                direction: SortDirectionEnum.DESC,
                column: PartnerListColumnSortable.ID,
              },
            },
          })}
        />
      ),
      user: user && (
        <UserLink
          disabled={!can(AccessEnums.AdminUsersList)}
          user={{
            id: user.id,
            name: user.name,
            mobile: user.mobile,
          }}
        />
      ),
      url: (
        <span
          title={url}
          style={{
            width: "200px",
            overflow: "hidden",
            whiteSpace: "nowrap",
            display: "inline-block",
            textOverflow: "ellipsis",
          }}
        >
          {url}
        </span>
      ),
      response_code: response_code && (
        <InfoBadge origin={OriginEnum.STATUS_CODE} status={response_code} />
      ),
      operations: (
        <Stack direction="row" alignItems="center" justifyContent="center">
          <Tooltip title={seenLogIds.has(id) ? "مشاهده شده" : "مشاهده جزئیات"}>
            <IconButton
              onClick={() => handleOpenLogModal(id)}
              disabled={!can(AccessEnums.AdminLogsDetails)}
            >
              <VisibilityIcon
                color={seenLogIds.has(id) ? "success" : "action"}
              />
              {seenLogIds.has(id) && (
                <CheckCircleIcon
                  color="success"
                  fontSize="small"
                  sx={{ ml: 0.5 }}
                />
              )}
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    }),
  );
}
export default function LogsTableComponent() {
  const { parseQueryString, queryStringUrl, setParamWithSearchParams } =
    useUrl();
  const parsed = parseQueryString();
  const initialPage = parsed.page ? Number(parsed.page) : 1;
  const initialRows = parsed.count ? Number(parsed.count) : 100;
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);
  const [total, setTotal] = useState(0);
  const [reload, setReload] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [rawLogs, setRawLogs] = useState<any[]>([]);
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [seenLogIds, setSeenLogIds] = useState<Set<number>>(new Set());
  const [selectedLogId, setSelectedLogId] = useState<number | null>(null);

  const location = useLocation();
  const { can } = useCanAccess();
  const { lang } = useLangContext();
  const { setNotification } = useNotificationContext();
  const protectedInstance = useProtectedAxiosInstance();

  const { parseUrlSorts, applyUrlSorts } = useUrlSort();
  const sorts = parseUrlSorts();

  const handleOpenLogModal = useCallback((id: number) => {
    setSelectedLogId(id);
    setLogModalOpen(true);
    setSeenLogIds((prev) => {
      const updated = new Set(prev);
      updated.add(id);
      return updated;
    });
  }, []);

  const getData = async () => {
    try {
      setWaiting(true);
      const logListRequest = new LogListRequest(protectedInstance);
      const resp = await logListRequest.send({
        page,
        count: rowsPerPage,
        ...parseQueryString(),
      });
      setTotal(resp.data.total);
      setRawLogs(resp.data.logs);
    } catch (error) {
      errorResolver({ error, setNotification });
    } finally {
      setWaiting(false);
    }
  };

  useEffect(() => {
    getData();
  }, [page, rowsPerPage, location.search, reload]);

  const logsData = useMemo(
    () =>
      buildLogRows(
        rawLogs,
        seenLogIds,
        can,
        handleOpenLogModal,
        queryStringUrl,
      ),
    [rawLogs, seenLogIds],
  );

  const logTableColumns: ITableSchema[] = [
    {
      key: "id",
      minWidth: 100,
      sortable: true,
      label: "شناسه",
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      minWidth: 170,
      align: "center",
      label: "زمان درخواست",
      key: "requested_at_millisecond",
      FilterComponent: DateTimeFilter,
      format: (value: string) =>
        value &&
        formatDateWithTime({ lang, date: new Date(value).toISOString() }),
    },
    {
      key: "user",
      minWidth: 170,
      label: "کاربر",
      align: "center",
    },
    {
      minWidth: 100,
      key: "user_id",
      align: "center",
      label: "شناسه کاربر",
      FilterComponent: NumberFilter,
    },
    {
      minWidth: 150,
      align: "center",
      key: "partner_id",
      label: "شناسه شریک",
      FilterComponent: NumberFilter,
    },
    {
      minWidth: 100,
      align: "center",
      key: "workspace_id",
      label: "شناسه تخته",
      FilterComponent: NumberFilter,
    },
    {
      minWidth: 150,
      align: "center",
      key: "access_token_id",
      label: "شناسه توکن دسترسی",
      FilterComponent: NumberFilter,
    },
    {
      key: "model",
      label: "مدل",
      minWidth: 150,
      visible: false,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      minWidth: 150,
      visible: false,
      align: "center",
      label: "رفرنس مدل",
      key: "reference_model",
      FilterComponent: NumberFilter,
    },
    {
      key: "url",
      label: "آدرس",
      minWidth: 200,
      align: "left",
      FilterComponent: TextFilter,
    },
    {
      label: "متد",
      minWidth: 120,
      align: "center",
      key: "request_method",
      FilterComponent: SelectionFilter,
      options: {
        get: "get",
        put: "put",
        post: "post",
        patch: "patch",
        delete: "delete",
      },
    },
    {
      minWidth: 130,
      align: "center",
      label: "کد پاسخ",
      key: "response_code",
      FilterComponent: NumberFilter,
    },
    {
      minWidth: 120,
      align: "center",
      label: "مدت (ms)",
      key: "duration_millisecond",
      FilterComponent: NumberFilter,
    },
    {
      minWidth: 220,
      align: "center",
      key: "track_code",
      label: "کد پیگیری",
      FilterComponent: TextFilter,
    },
    {
      minWidth: 100,
      align: "center",
      label: "عملیات",
      key: "operations",
    },
  ];

  const { filtersSchema, columns } = useTableSchema(logTableColumns);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setParamWithSearchParams("page", String(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRows = +event.target.value;
    setRowsPerPage(newRows);
    setParamWithSearchParams("count", String(newRows));
  };

  return (
    <Box sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}>
      <CustomTable
        page={page}
        sorts={sorts}
        count={total}
        rows={logsData}
        hasSearchFilter
        hasTopPagination
        columns={columns}
        hasBottomPagination
        hasWaiting={waiting}
        rowsPerPage={rowsPerPage}
        onApplySorts={applyUrlSorts}
        filtersSchema={filtersSchema}
        handleChangePage={handleChangePage}
        reloadAction={() => setReload(!reload)}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />
      <LogDetailsModal
        open={logModalOpen}
        onClose={() => {
          setLogModalOpen(false);
        }}
        logId={selectedLogId?.toString()}
      />
    </Box>
  );
}
